import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { ColorPicker, parseColorValue, colorValueToCss } from './ColorPicker';
import { useSavedColors } from '../../context/SavedColorsContext';

/**
 * ColorField - Color picker with popover
 *
 * @param {Object} props
 * @param {Object} props.field - Field configuration
 * @param {string} props.field.key - Unique field identifier
 * @param {string} props.field.label - Display label
 * @param {string} [props.field.default] - Default color value
 * @param {boolean} [props.field.allowTransparent] - Whether to allow transparent value
 * @param {boolean} [props.field.allowGradient] - Whether to allow gradient values (default: false)
 * @param {string} [props.field.description] - Optional description text
 * @param {string|Object} props.value - Current color value (hex format, 'transparent', or object format)
 * @param {Function} props.onChange - Callback when value changes
 * @param {boolean} [props.disabled] - Whether field is disabled
 * @param {string} [props.error] - Error message to display
 * @param {boolean} [props.compact] - Use compact inline layout (default: false)
 * @param {Array} [props.savedColors] - Saved page colors
 * @param {Function} [props.onSaveColor] - Save color to page (value: object, label: string) => void
 * @param {Function} [props.onDeleteSavedColor] - Delete saved color (id: string) => void
 */
// Resize constraints
const MIN_WIDTH = 320;
const MIN_HEIGHT = 300;
const MAX_WIDTH = 800;
const MAX_HEIGHT = 800;
const DEFAULT_WIDTH = 480;

export default function ColorField({
  field,
  value,
  onChange,
  disabled = false,
  error,
  compact = false,
  savedColors: propSavedColors,
  onSaveColor: propOnSaveColor,
  onDeleteSavedColor: propOnDeleteSavedColor,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const popoverRef = useRef(null);

  // Draggable modal state
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Resizable modal state
  const [size, setSize] = useState({ width: DEFAULT_WIDTH, height: 'auto' });
  const [isResizing, setIsResizing] = useState(false); // false, 'right', 'bottom', or 'corner'
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });

  // Get saved colors from context as fallback
  const contextSavedColors = useSavedColors();

  // Use props if provided, otherwise fall back to context
  const savedColors = propSavedColors !== undefined ? propSavedColors : contextSavedColors.savedColors;
  const onSaveColor = propOnSaveColor !== undefined ? propOnSaveColor : contextSavedColors.onSaveColor;
  const onDeleteSavedColor = propOnDeleteSavedColor !== undefined ? propOnDeleteSavedColor : contextSavedColors.onDeleteSavedColor;

  // Track if original value was a string (for backwards compat)
  const isStringInput = typeof value === 'string' || value === undefined || value === null;

  // Parse the current value
  const parsedValue = useMemo(() => {
    if (value === undefined || value === null) {
      return parseColorValue(field.default || '#000000');
    }
    return parseColorValue(value);
  }, [value, field.default]);

  // Get CSS value for display
  const cssValue = useMemo(() => colorValueToCss(parsedValue), [parsedValue]);

  // Check if transparent
  const isTransparent = parsedValue.type === 'solid' && parsedValue.alpha === 0;

  // Check if gradient
  const isGradient = parsedValue.type === 'linear-gradient' || parsedValue.type === 'radial-gradient';

  // Checkered pattern for transparency backgrounds
  const checkeredBg = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16'%3E%3Crect x='0' y='0' width='8' height='8' fill='%23ccc'/%3E%3Crect x='8' y='8' width='8' height='8' fill='%23ccc'/%3E%3Crect x='8' y='0' width='8' height='8' fill='%23fff'/%3E%3Crect x='0' y='8' width='8' height='8' fill='%23fff'/%3E%3C/svg%3E")`;

  // Get display text for the color value
  const displayText = useMemo(() => {
    if (isTransparent) {
      return 'transparent';
    }
    if (isGradient) {
      return parsedValue.type === 'linear-gradient' ? 'Linear gradient' : 'Radial gradient';
    }
    return parsedValue.color || '#000000';
  }, [isTransparent, isGradient, parsedValue]);

  // Handle color change from picker
  const handleColorChange = useCallback((newValue) => {
    // For backwards compatibility:
    // - If original input was a string and new value is a solid color with 100% alpha, output string
    // - Otherwise output object format
    if (isStringInput && newValue.type === 'solid') {
      if (newValue.alpha === 0) {
        onChange('transparent');
      } else if (newValue.alpha === 100) {
        onChange(newValue.color);
      } else {
        // Has alpha, need object format
        onChange(newValue);
      }
    } else {
      onChange(newValue);
    }
  }, [isStringInput, onChange]);

  // Handle clear button click
  const handleClear = useCallback((e) => {
    e.stopPropagation();
    if (isStringInput) {
      onChange('transparent');
    } else {
      onChange({ type: 'solid', color: parsedValue.color || '#000000', alpha: 0 });
    }
  }, [isStringInput, onChange, parsedValue.color]);

  // Close handler for the modal
  const handleClose = useCallback(() => {
    setIsOpen(false);
    setPosition({ x: 0, y: 0 }); // Reset position when closing
  }, []);

  // Drag handlers for modal
  const handleDragStart = useCallback((e) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  }, [position.x, position.y]);

  const handleDrag = useCallback((e) => {
    if (!isDragging) return;
    setPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  }, [isDragging, dragStart.x, dragStart.y]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Resize handlers for modal
  const handleResizeStart = useCallback((e, direction) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(direction); // 'right', 'bottom', 'corner'
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: typeof size.height === 'number' ? size.height : popoverRef.current?.offsetHeight || 400
    });
  }, [size.width, size.height]);

  const handleResize = useCallback((e) => {
    if (!isResizing) return;

    let newWidth = resizeStart.width;
    let newHeight = resizeStart.height;

    if (isResizing === 'right' || isResizing === 'corner') {
      newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, resizeStart.width + (e.clientX - resizeStart.x)));
    }
    if (isResizing === 'bottom' || isResizing === 'corner') {
      newHeight = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, resizeStart.height + (e.clientY - resizeStart.y)));
    }

    setSize({ width: newWidth, height: newHeight });
  }, [isResizing, resizeStart]);

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
  }, []);

  // Global mouse event listeners for dragging
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      handleDrag(e);
    };

    const handleMouseUp = () => {
      handleDragEnd();
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleDrag, handleDragEnd]);

  // Global mouse event listeners for resizing
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e) => {
      handleResize(e);
    };

    const handleMouseUp = () => {
      handleResizeEnd();
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, handleResize, handleResizeEnd]);

  // Reset position and size when modal opens
  useEffect(() => {
    if (isOpen) {
      setPosition({ x: 0, y: 0 });
      setSize({ width: DEFAULT_WIDTH, height: 'auto' });
    }
  }, [isOpen]);

  // Handle escape key to close popover
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Compact mode: inline label + color swatch + value text
  if (compact) {
    return (
      <div className="relative" ref={containerRef}>
        <div className="flex items-center gap-2 py-0.5">
          <label className="text-xs text-gray-500 w-16 flex-shrink-0 truncate">
            {field.label}
          </label>
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            {/* Color swatch (clickable) */}
            <button
              type="button"
              onClick={() => !disabled && setIsOpen(!isOpen)}
              disabled={disabled}
              className={`w-7 h-7 rounded border border-gray-200 flex-shrink-0 overflow-hidden
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-gray-300'}`}
              style={{
                backgroundImage: checkeredBg,
                backgroundColor: '#fff',
              }}
            >
              <div
                className="w-full h-full"
                style={{
                  background: cssValue,
                }}
              />
            </button>

            {/* Color value text */}
            <span
              className={`text-xs truncate flex-1 min-w-0 ${
                isTransparent ? 'text-blue-600 font-medium' : 'text-gray-600'
              }`}
              title={cssValue}
            >
              {displayText}
            </span>

            {/* Clear button */}
            {field.allowTransparent && !disabled && !isTransparent && (
              <button
                type="button"
                onClick={handleClear}
                className="px-1.5 py-0.5 text-xs text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded flex-shrink-0"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Color Picker Modal (Portal) */}
        {isOpen && createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center animate-in fade-in duration-150"
          >
            {/* Transparent backdrop for click-to-close */}
            <div
              className="absolute inset-0"
              onClick={handleClose}
            />
            {/* Modal */}
            <div
              ref={popoverRef}
              className="relative bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col"
              style={{
                width: size.width,
                height: typeof size.height === 'number' ? size.height : 'auto',
                minHeight: MIN_HEIGHT,
                transform: `translate(${position.x}px, ${position.y}px)`
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Draggable header */}
              <div
                className="flex items-center justify-between px-4 py-3 border-b border-gray-200 cursor-move select-none bg-gray-50 rounded-t-xl flex-shrink-0"
                onMouseDown={handleDragStart}
              >
                <span className="text-sm font-medium text-gray-700">Color Picker</span>
                <button
                  type="button"
                  onClick={handleClose}
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
              {/* Content with scroll */}
              <div className="p-4 overflow-y-auto flex-1">
                <ColorPicker
                  value={parsedValue}
                  onChange={handleColorChange}
                  allowGradient={field.allowGradient || false}
                  allowTransparent={field.allowTransparent !== false}
                  savedColors={savedColors}
                  onSaveColor={onSaveColor}
                  onDeleteSavedColor={onDeleteSavedColor}
                />
              </div>

              {/* Resize handles */}
              {/* Right edge resize handle */}
              <div
                className="absolute top-0 right-0 w-2 h-full cursor-ew-resize hover:bg-blue-500/20 transition-colors"
                onMouseDown={(e) => handleResizeStart(e, 'right')}
              />
              {/* Bottom edge resize handle */}
              <div
                className="absolute bottom-0 left-0 w-full h-2 cursor-ns-resize hover:bg-blue-500/20 transition-colors"
                onMouseDown={(e) => handleResizeStart(e, 'bottom')}
              />
              {/* Corner resize handle */}
              <div
                className="absolute bottom-0 right-0 w-5 h-5 cursor-se-resize group"
                onMouseDown={(e) => handleResizeStart(e, 'corner')}
              >
                <svg
                  className="w-full h-full text-gray-300 group-hover:text-gray-500 transition-colors"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M18 18L18 10M18 18L10 18M14 18L18 14"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    );
  }

  // Standard mode
  return (
    <div className="relative" ref={containerRef}>
      <label className="block text-xs font-medium text-gray-700 mb-2">
        {field.label}
      </label>
      <div className="flex items-center gap-2">
        {/* Color swatch (clickable) */}
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`w-8 h-8 rounded border border-gray-300 flex-shrink-0 overflow-hidden
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-gray-400'}`}
          style={{
            backgroundImage: checkeredBg,
            backgroundColor: '#fff',
          }}
        >
          <div
            className="w-full h-full"
            style={{
              background: cssValue,
            }}
          />
        </button>

        {/* Color value text */}
        <span
          className={`flex-1 px-2 py-1 text-xs border rounded bg-gray-50 truncate ${
            isTransparent ? 'text-blue-600 font-medium' : 'text-gray-700'
          } ${
            error
              ? 'border-red-500'
              : 'border-gray-300'
          } ${disabled ? 'bg-gray-100' : ''}`}
          title={cssValue}
        >
          {displayText}
        </span>

        {/* Clear button */}
        {field.allowTransparent && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className={`px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 ${
              isTransparent ? 'bg-gray-100 text-gray-700' : ''
            }`}
          >
            Clear
          </button>
        )}
      </div>

      {field.description && (
        <p className="mt-1 text-xs text-gray-500">{field.description}</p>
      )}
      {error && (
        <p className="mt-1 text-xs text-red-500">{error}</p>
      )}

      {/* Color Picker Modal (Portal) */}
      {isOpen && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center animate-in fade-in duration-150"
        >
          {/* Transparent backdrop for click-to-close */}
          <div
            className="absolute inset-0"
            onClick={handleClose}
          />
          {/* Modal */}
          <div
            ref={popoverRef}
            className="relative bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col"
            style={{
              width: size.width,
              height: typeof size.height === 'number' ? size.height : 'auto',
              minHeight: MIN_HEIGHT,
              transform: `translate(${position.x}px, ${position.y}px)`
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Draggable header */}
            <div
              className="flex items-center justify-between px-4 py-3 border-b border-gray-200 cursor-move select-none bg-gray-50 rounded-t-xl flex-shrink-0"
              onMouseDown={handleDragStart}
            >
              <span className="text-sm font-medium text-gray-700">Color Picker</span>
              <button
                type="button"
                onClick={handleClose}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
              >
                <X size={16} />
              </button>
            </div>
            {/* Content with scroll */}
            <div className="p-4 overflow-y-auto flex-1">
              <ColorPicker
                value={parsedValue}
                onChange={handleColorChange}
                allowGradient={field.allowGradient || false}
                allowTransparent={field.allowTransparent !== false}
                savedColors={savedColors}
                onSaveColor={onSaveColor}
                onDeleteSavedColor={onDeleteSavedColor}
              />
            </div>

            {/* Resize handles */}
            {/* Right edge resize handle */}
            <div
              className="absolute top-0 right-0 w-2 h-full cursor-ew-resize hover:bg-blue-500/20 transition-colors"
              onMouseDown={(e) => handleResizeStart(e, 'right')}
            />
            {/* Bottom edge resize handle */}
            <div
              className="absolute bottom-0 left-0 w-full h-2 cursor-ns-resize hover:bg-blue-500/20 transition-colors"
              onMouseDown={(e) => handleResizeStart(e, 'bottom')}
            />
            {/* Corner resize handle */}
            <div
              className="absolute bottom-0 right-0 w-5 h-5 cursor-se-resize group"
              onMouseDown={(e) => handleResizeStart(e, 'corner')}
            >
              <svg
                className="w-full h-full text-gray-300 group-hover:text-gray-500 transition-colors"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M18 18L18 10M18 18L10 18M14 18L18 14"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
